import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Hls from "hls.js";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Slider,
  Stack,
  CircularProgress,
  Box,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import LockIcon from "@mui/icons-material/Lock";
import DoneIcon from "@mui/icons-material/Done";
import ApiService from "../../services/api-service";
import notifier from "../Notifier";
import { getErrorString, getUserId } from "../../constants/function";
import NoData from "../Nodata";

const formatSeconds = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const SONG_COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#DDA0DD', '#F4A460'];

export default function ClipEditor({
  open,
  onClose,
  channelName,
  livestreamId,
  songData,
  songTimelineRef,
  hlsUrl: hlsUrlProp,
}) {
  // Capture the song timeline + open timestamp when the dialog opens
  const [capturedTimeline, setCapturedTimeline] = useState([]);
  const [dialogOpenedAt, setDialogOpenedAt] = useState(null);
  useEffect(() => {
    if (open) {
      setDialogOpenedAt(Date.now());
      // Read from the ref and include the current song as fallback
      const timeline = songTimelineRef?.current?.length
        ? [...songTimelineRef.current]
        : songData?._id ? [{ _id: songData._id, title: songData.title || '', wallTime: 0 }] : [];
      console.log('[ClipEditor] Captured timeline:', JSON.stringify(timeline.map(s => ({ id: s._id, title: s.title, wallTime: s.wallTime ? new Date(s.wallTime).toISOString() : 'epoch' }))));
      console.log('[ClipEditor] songTimelineRef.current length:', songTimelineRef?.current?.length, 'songData:', songData?._id);
      setCapturedTimeline(timeline);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const [title, setTitle] = useState("");
  const [range, setRange] = useState([30, 60]);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [loading, setLoading] = useState(false);

  // Preview state (HLS mode)
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const previewStartRef = useRef(null);
  const [previewReady, setPreviewReady] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewMuted, setPreviewMuted] = useState(true);
  const [previewElapsed, setPreviewElapsed] = useState(0);

  const initialSeekDoneRef = useRef(false);
  const [seekLimited, setSeekLimited] = useState(false);

  // Recording mode state (fallback when HLS preview fails)
  const [recordingMode, setRecordingMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const clipMediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const captureStreamRef = useRef(null);
  const durationRef = useRef(0);

  // Filmstrip thumbnails — captured every 3s from the live video
  const [thumbnails, setThumbnails] = useState([]);
  const thumbnailsRef = useRef([]);
  const THUMBNAIL_SLOTS = 30;

  // Dynamic buffer: measured from actual HLS seekable range instead of hardcoded 90s
  const [bufferDuration, setBufferDuration] = useState(null);
  const bufferInitRef = useRef(false);
  const maxBuffer = bufferDuration != null ? Math.min(90, bufferDuration) : 90;
  const minClip = 5;
  const maxClip = 60;
  const clipDuration = range[1] - range[0];

  // Compute all songs in the timeline with their display positions on the filmstrip.
  // Each gets a displayStart/displayEnd (0 = oldest/left, maxBuffer = now/right) and a color.
  const timelineSongs = useMemo(() => {
    if (!capturedTimeline.length || !dialogOpenedAt) return [];

    return capturedTimeline.map((entry, i) => {
      const songStartMs = entry.wallTime;
      const songEndMs = i + 1 < capturedTimeline.length
        ? capturedTimeline[i + 1].wallTime
        : dialogOpenedAt;

      // Wall-clock → display position: displayPos = maxBuffer - secsAgo
      const displayStart = Math.max(0, Math.min(maxBuffer, maxBuffer - (dialogOpenedAt - songStartMs) / 1000));
      const displayEnd = Math.max(0, Math.min(maxBuffer, maxBuffer - (dialogOpenedAt - songEndMs) / 1000));

      return { ...entry, displayStart, displayEnd, color: SONG_COLORS[i % SONG_COLORS.length] };
    });
  }, [capturedTimeline, dialogOpenedAt, maxBuffer]);

  // Filter to only songs overlapping the current clip range
  const overlappingSongs = useMemo(() => {
    if (!timelineSongs.length) return [];
    const clipDisplayStart = maxBuffer - range[1]; // left edge
    const clipDisplayEnd = maxBuffer - range[0];   // right edge
    return timelineSongs.filter(s => s.displayStart < clipDisplayEnd && s.displayEnd > clipDisplayStart);
  }, [timelineSongs, range]);

  // Use server-provided HLS URL if available, otherwise construct from channelName
  const hlsUrl = hlsUrlProp
    || (channelName
      ? `${process.env.REACT_APP_HLS_BASE_URL || "https://staging-media.siki.io/hls"}/${channelName}.m3u8`
      : null);

  // Callback ref — fires when the <video> element mounts into the DOM
  const videoCallbackRef = useCallback(
    (node) => {
      // Teardown previous instance if any
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      videoRef.current = node;
      if (!node || !hlsUrl) return;

      if (!Hls.isSupported()) {
        // Safari native HLS
        node.src = hlsUrl;
        node.muted = true;
        node.play().catch(() => {});
        setPreviewReady(true);
        return;
      }

      const hls = new Hls({
        liveBackBufferLength: 120,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 6,
        liveDurationInfinity: true,
        enableWorker: true,
        lowLatencyMode: false,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.1,
        audioCodec: "mp4a.40.2",
        startPosition: -1,
        xhrSetup: (xhr) => { xhr.withCredentials = false; },
        fLoader: class extends Hls.DefaultConfig.loader {
          load(context, config, callbacks) {
            if (context.url && context.url.includes(".ts")) {
              const sep = context.url.includes("?") ? "&" : "?";
              context.url = `${context.url}${sep}_t=${Date.now()}`;
            }
            return super.load(context, config, callbacks);
          }
        },
        pLoader: class extends Hls.DefaultConfig.loader {
          load(context, config, callbacks) {
            if (context.url && context.url.includes(".m3u8")) {
              const sep = context.url.includes("?") ? "&" : "?";
              context.url = `${context.url}${sep}_t=${Date.now()}`;
            }
            return super.load(context, config, callbacks);
          }
        },
      });

      hlsRef.current = hls;
      const sourceUrl = `${hlsUrl}?t=${Date.now()}`;
      console.log("[ClipPreview] Loading HLS:", sourceUrl);
      hls.loadSource(sourceUrl);
      hls.attachMedia(node);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setPreviewReady(true);
        setPreviewError(false);
        node.muted = true;
        node.play().catch(() => {});
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return;
        console.warn("[ClipPreview] Fatal HLS error:", data.type, data.details);
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          // Network error (404 manifest, CORS, no HLS stream) — show fallback
          setPreviewError(true);
        }
      });
    },
    [hlsUrl],
  );

  // Activate recording mode when HLS preview fails
  useEffect(() => {
    if (previewError && open) {
      setRecordingMode(true);
    }
  }, [previewError, open]);

  // Cleanup when dialog closes
  useEffect(() => {
    if (!open) {
      // HLS cleanup
      setPreviewReady(false);
      setPreviewError(false);
      setPreviewPlaying(false);
      setPreviewElapsed(0);
      setSeekLimited(false);
      previewStartRef.current = null;
      initialSeekDoneRef.current = false;
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      // Recording cleanup
      setRecordingMode(false);
      setIsRecording(false);
      durationRef.current = 0;
      setRecordingDuration(0);
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      setRecordedBlob(null);
      setRecordedUrl(null);
      if (clipMediaRecorderRef.current && clipMediaRecorderRef.current.state !== 'inactive') {
        clipMediaRecorderRef.current.stop();
      }
      if (captureStreamRef.current) {
        captureStreamRef.current.getTracks().forEach(t => t.stop());
        captureStreamRef.current = null;
      }
      clearInterval(recordingTimerRef.current);
      // Buffer + thumbnail cleanup
      setBufferDuration(null);
      bufferInitRef.current = false;
      thumbnailsRef.current = [];
      setThumbnails([]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Measure actual HLS buffer and set maxBuffer dynamically
  useEffect(() => {
    if (!previewReady || recordingMode) return;

    const checkBuffer = () => {
      const video = videoRef.current;
      const hls = hlsRef.current;
      if (!video || !hls) return;

      const livePos = hls.liveSyncPosition;
      if (livePos == null || video.seekable.length === 0) return;

      const seekStart = video.seekable.start(0);
      const available = Math.floor(livePos - seekStart);
      if (available > 0) {
        setBufferDuration((prev) => Math.min(90, Math.max(prev || 0, available)));
      }
    };

    // Initial check after buffer accumulates, then periodic updates
    const timeout = setTimeout(checkBuffer, 2000);
    const interval = setInterval(checkBuffer, 5000);
    return () => { clearTimeout(timeout); clearInterval(interval); };
  }, [previewReady, recordingMode]);

  // Reset clip range to fit actual buffer when first measured
  useEffect(() => {
    if (bufferDuration != null && !bufferInitRef.current) {
      bufferInitRef.current = true;
      const defaultDuration = Math.min(30, bufferDuration);
      setRange([0, defaultDuration]);
    }
  }, [bufferDuration]);

  // Seek preview to clip start when range changes (while not playing)
  useEffect(() => {
    if (!previewReady || previewPlaying) return;

    // Skip auto-seek on initial dialog open — let video play at live edge
    if (!initialSeekDoneRef.current) {
      initialSeekDoneRef.current = true;
      return;
    }

    const video = videoRef.current;
    const hls = hlsRef.current;
    if (!video || !hls) return;

    const livePos = hls.liveSyncPosition;
    if (livePos == null) return;

    const seekTarget = livePos - range[1];
    if (seekTarget >= 0 && video.seekable.length > 0) {
      const seekableStart = video.seekable.start(0);
      if (seekTarget >= seekableStart) {
        video.currentTime = seekTarget;
        setSeekLimited(false);
      } else {
        // Can't seek that far back — clamp to earliest available
        video.currentTime = seekableStart;
        setSeekLimited(true);
      }
    }
    setPreviewElapsed(0);
  }, [range, previewReady, previewPlaying]);

  // Capture video frames for the filmstrip by seeking through the HLS buffer.
  // Only captures historical frames — the clip range is fixed to when the dialog opened.
  useEffect(() => {
    if (!previewReady || !videoRef.current || recordingMode) return;

    const canvas = document.createElement('canvas');
    canvas.width = 160;
    canvas.height = 90;
    const ctx = canvas.getContext('2d');
    let cancelled = false;

    const grabFrame = () => {
      const video = videoRef.current;
      if (!video || video.readyState < 2) return null;
      try {
        ctx.drawImage(video, 0, 0, 160, 90);
        return canvas.toDataURL('image/jpeg', 0.5);
      } catch (_) { return null; }
    };

    const waitForSeek = (video) => new Promise((resolve) => {
      const done = () => resolve();
      video.addEventListener('seeked', done, { once: true });
      setTimeout(done, 400);
    });

    (async () => {
      const video = videoRef.current;
      const hls = hlsRef.current;
      if (!video || !hls) return;

      // Wait for buffer to accumulate
      await new Promise(r => setTimeout(r, 1500));
      if (cancelled) return;

      const livePos = hls.liveSyncPosition;
      if (livePos == null || video.seekable.length === 0) return;

      const seekStart = video.seekable.start(0);
      const bufferSecs = livePos - seekStart;
      if (bufferSecs < 6) return;

      const numFrames = Math.min(THUMBNAIL_SLOTS, Math.floor(bufferSecs / 3));
      const step = bufferSecs / numFrames;
      const frames = [];

      video.pause();
      for (let i = 0; i < numFrames && !cancelled; i++) {
        video.currentTime = seekStart + (i * step);
        await waitForSeek(video);
        if (cancelled) break;
        const frame = grabFrame();
        if (frame) frames.push(frame);
      }

      if (!cancelled && frames.length > 0) {
        thumbnailsRef.current = frames;
        setThumbnails([...frames]);
      }

      // Seek back to live edge and resume playback
      if (!cancelled) {
        video.currentTime = livePos;
        video.play().catch(() => {});
      }
    })();

    return () => { cancelled = true; };
  }, [previewReady, recordingMode, THUMBNAIL_SLOTS]);

  // Auto-pause at clip boundary
  const handleTimeUpdate = useCallback(() => {
    if (!previewPlaying || previewStartRef.current == null) return;
    const video = videoRef.current;
    if (!video) return;

    const elapsed = video.currentTime - previewStartRef.current;
    setPreviewElapsed(Math.max(0, Math.min(elapsed, clipDuration)));

    if (elapsed >= clipDuration) {
      video.pause();
      setPreviewPlaying(false);
      // Seek back to clip start (with seekable range check)
      const hls = hlsRef.current;
      if (hls && hls.liveSyncPosition != null) {
        let seekBack = hls.liveSyncPosition - range[1];
        if (video.seekable.length > 0) {
          const seekableStart = video.seekable.start(0);
          if (seekBack < seekableStart) seekBack = seekableStart;
        }
        video.currentTime = seekBack;
      }
      setPreviewElapsed(0);
      previewStartRef.current = null;
    }
  }, [previewPlaying, clipDuration, range]);

  const togglePreview = () => {
    const video = videoRef.current;
    const hls = hlsRef.current;
    if (!video || !hls) return;

    if (previewPlaying) {
      video.pause();
      setPreviewPlaying(false);
    } else {
      const livePos = hls.liveSyncPosition;
      if (livePos == null) return;
      let seekTarget = Math.max(0, livePos - range[1]);

      // Clamp to seekable range if the target is too far back
      if (video.seekable.length > 0) {
        const seekableStart = video.seekable.start(0);
        if (seekTarget < seekableStart) {
          seekTarget = seekableStart;
          setSeekLimited(true);
        } else {
          setSeekLimited(false);
        }
      }

      video.currentTime = seekTarget;
      previewStartRef.current = seekTarget;
      setPreviewElapsed(0);
      video.play().catch(() => {});
      setPreviewPlaying(true);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !previewMuted;
      setPreviewMuted(!previewMuted);
    }
  };

  // Playback position in display space — moves left to right as clip plays
  const displayPlaybackPos = previewPlaying && previewElapsed > 0
    ? maxBuffer - range[1] + previewElapsed
    : null;

  // --- Recording mode functions ---

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
        audio: true,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        systemAudio: 'include',
      });

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8'
        : 'video/webm';

      const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5000000 });
      recordedChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        setIsRecording(false);
        clearInterval(recordingTimerRef.current);
        stream.getTracks().forEach(t => t.stop());
      };

      // If user stops sharing via browser UI
      stream.getVideoTracks()[0].onended = () => {
        if (clipMediaRecorderRef.current && clipMediaRecorderRef.current.state !== 'inactive') {
          clipMediaRecorderRef.current.stop();
        }
      };

      clipMediaRecorderRef.current = recorder;
      captureStreamRef.current = stream;
      recorder.start(1000);
      setIsRecording(true);
      durationRef.current = 0;
      setRecordingDuration(0);
      setRecordedBlob(null);
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      setRecordedUrl(null);

      recordingTimerRef.current = setInterval(() => {
        durationRef.current += 1;
        setRecordingDuration(durationRef.current);
        if (durationRef.current >= 60) {
          stopRecording();
        }
      }, 1000);
    } catch (err) {
      if (err.name !== 'NotAllowedError') {
        notifier.error("Failed to start recording: " + err.message);
      }
    }
  };

  const stopRecording = () => {
    if (clipMediaRecorderRef.current && clipMediaRecorderRef.current.state !== 'inactive') {
      clipMediaRecorderRef.current.stop();
    }
    clearInterval(recordingTimerRef.current);
  };

  const resetRecording = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    durationRef.current = 0;
    setRecordingDuration(0);
  };

  // Fetch playlists
  useEffect(() => {
    if (!open) return;
    const fetchPlaylists = async () => {
      try {
        setLoading(true);
        const response = await ApiService.PLAYLIST_ALL_(getUserId());
        setPlaylists(response?.result || []);
      } catch (error) {
        notifier.error(getErrorString(error));
      } finally {
        setLoading(false);
      }
    };
    fetchPlaylists();
  }, [open]);

  // Display range: convert internal "seconds ago" to left-to-right timeline
  // Slider: 0 (left) = oldest, maxBuffer (right) = now
  const displayRange = [maxBuffer - range[1], maxBuffer - range[0]];

  const handleRangeChange = (_, newDisplay) => {
    // Convert display positions back to internal "seconds ago"
    const newRange = [maxBuffer - newDisplay[1], maxBuffer - newDisplay[0]];
    const dur = newRange[1] - newRange[0];
    if (dur >= minClip && dur <= maxClip) {
      setRange(newRange);
    }
  };

  const handlePublish = () => {
    if (!title.trim()) {
      notifier.error("Please enter a title for your clip");
      return;
    }
    if (!selectedPlaylist) {
      notifier.error("Please select a playlist");
      return;
    }

    // Pause preview before publishing
    if (previewPlaying && videoRef.current) {
      videoRef.current.pause();
      setPreviewPlaying(false);
    }

    // Build the request but don't await — close dialog immediately
    let request;
    if (recordingMode && recordedBlob) {
      const formData = new FormData();
      formData.append('video', recordedBlob, `clip-${Date.now()}.webm`);
      formData.append('title', title.trim());
      formData.append('studioId', selectedPlaylist);
      if (livestreamId) formData.append('livestreamId', livestreamId);
      if (overlappingSongs.length) {
        formData.append('musicIds', JSON.stringify(overlappingSongs.map(s => s._id)));
        formData.append('musicId', overlappingSongs[0]._id);
      }
      request = ApiService.UPLOAD_CLIP(formData);
    } else {
      const data = {
        channelName,
        startOffset: range[1],
        duration: clipDuration,
        title: title.trim(),
        studioId: selectedPlaylist,
        livestreamId,
      };
      if (overlappingSongs.length) {
        data.musicIds = overlappingSongs.map(s => s._id);
        data.musicId = overlappingSongs[0]._id;
        data.musicTitle = overlappingSongs.map(s => s.title || "Unknown").join(", ");
      }
      request = ApiService.CREATE_CLIP(data);
    }

    // Close dialog and notify — processing happens in background
    notifier.success("Creating clip... You'll be notified when it's ready.");
    onClose();

    request
      .then(() => notifier.success("Clip published!"))
      .catch((error) => notifier.error(getErrorString(error) || "Failed to create clip"));
  };

  // Determine if publish button should be enabled
  const canPublish = recordingMode
    ? !!(recordedBlob && recordingDuration >= minClip && title.trim() && selectedPlaylist)
    : !!(title.trim() && selectedPlaylist);

  return (
    <Dialog
      open={open}
      onClose={isRecording ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: "#1A1A1A",
          color: "#fff",
          border: "1px solid rgba(255,215,0,0.2)",
          borderRadius: "14px",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "var(--font-display)",
          fontSize: "14px",
          letterSpacing: "2px",
          color: "rgba(255,215,0,0.9)",
          textTransform: "uppercase",
          textAlign: "center",
          pb: 1,
        }}
      >
        Create Clip
      </DialogTitle>

      <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Video preview / Recording area */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            paddingTop: "56.25%", // 16:9
            backgroundColor: "#000",
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          {recordingMode ? (
            // --- Recording mode UI ---
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              {recordedUrl ? (
                // Show recorded clip preview
                <>
                  <video
                    src={recordedUrl}
                    controls
                    playsInline
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 5,
                    }}
                  >
                    <Button
                      onClick={resetRecording}
                      size="small"
                      sx={{
                        color: "#fff",
                        backgroundColor: "rgba(0,0,0,0.6)",
                        fontSize: "11px",
                        textTransform: "none",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
                      }}
                    >
                      Re-record
                    </Button>
                  </Box>
                  {recordingDuration < minClip && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "rgba(255,58,58,0.85)",
                        color: "#fff",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "11px",
                        zIndex: 5,
                      }}
                    >
                      Clip must be at least {minClip} seconds
                    </Box>
                  )}
                </>
              ) : isRecording ? (
                // Recording in progress
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: "#ff3a3a",
                        animation: "pulse 1.5s infinite",
                        "@keyframes pulse": {
                          "0%": { opacity: 1 },
                          "50%": { opacity: 0.4 },
                          "100%": { opacity: 1 },
                        },
                      }}
                    />
                    <Typography sx={{ color: "#ff3a3a", fontSize: "14px", fontWeight: 600 }}>
                      Recording
                    </Typography>
                  </Box>
                  <Typography sx={{ color: "#fff", fontSize: "24px", fontFamily: "monospace", fontWeight: 700 }}>
                    {recordingDuration}s / 60s
                  </Typography>
                  {recordingDuration < minClip && (
                    <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "11px" }}>
                      Minimum {minClip} seconds
                    </Typography>
                  )}
                  <Button
                    onClick={stopRecording}
                    disabled={recordingDuration < minClip}
                    sx={{
                      mt: 1,
                      color: "#fff",
                      backgroundColor: "rgba(255,58,58,0.7)",
                      textTransform: "none",
                      fontSize: "13px",
                      px: 3,
                      borderRadius: "8px",
                      "&:hover": { backgroundColor: "rgba(255,58,58,0.9)" },
                      "&.Mui-disabled": {
                        color: "rgba(255,255,255,0.3)",
                        backgroundColor: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    Stop Recording
                  </Button>
                </>
              ) : (
                // Ready to record
                <>
                  <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", textAlign: "center", px: 2 }}>
                    HLS preview unavailable — record directly from the live stream
                  </Typography>
                  <Button
                    onClick={startRecording}
                    sx={{
                      mt: 1,
                      color: "#fff",
                      backgroundColor: "rgba(255,58,58,0.6)",
                      textTransform: "none",
                      fontSize: "13px",
                      fontWeight: 600,
                      px: 3,
                      borderRadius: "8px",
                      "&:hover": { backgroundColor: "rgba(255,58,58,0.8)" },
                    }}
                  >
                    Start Recording
                  </Button>
                  <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "11px", mt: 0.5 }}>
                    {minClip}–60 seconds &middot; Captures your current tab
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            // --- HLS preview mode (existing) ---
            <>
              <video
                ref={videoCallbackRef}
                onTimeUpdate={handleTimeUpdate}
                playsInline
                muted={previewMuted}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: previewReady ? "block" : "none",
                }}
              />
              {!previewReady && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {previewError ? (
                    <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "13px", textAlign: "center", px: 2 }}>
                      Preview unavailable — you can still create the clip
                    </Typography>
                  ) : (
                    <>
                      <CircularProgress size={32} sx={{ color: "rgba(255,215,0,0.6)" }} />
                      <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>
                        Loading preview...
                      </Typography>
                    </>
                  )}
                </Box>
              )}
              {/* Seek-limited notice */}
              {previewReady && seekLimited && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(0,0,0,0.75)",
                    color: "rgba(255,215,0,0.9)",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "11px",
                    zIndex: 15,
                    whiteSpace: "nowrap",
                  }}
                >
                  Preview shows nearest available position — clip is created server-side
                </Box>
              )}
              {/* Preview controls overlay */}
              {previewReady && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 10px",
                    background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                  }}
                >
                  <Stack direction="row" alignItems="center" gap="4px">
                    <IconButton onClick={togglePreview} size="small" sx={{ color: "#fff" }}>
                      {previewPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                    </IconButton>
                    <IconButton onClick={toggleMute} size="small" sx={{ color: "#fff" }}>
                      {previewMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                    </IconButton>
                  </Stack>
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "12px", fontFamily: "monospace" }}>
                    {Math.floor(previewElapsed)}s / {clipDuration}s
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Timeline range selector with filmstrip — only for HLS mode */}
        {!recordingMode && (
          <Box>
            <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", mb: 1 }}>
              Clip Range
            </Typography>
            <Box sx={{ position: "relative", height: 56, borderRadius: "6px", overflow: "hidden" }}>
              {/* Filmstrip background — thumbnails captured from live video */}
              <Box sx={{ display: "flex", position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
                {Array.from({ length: THUMBNAIL_SLOTS }).map((_, i) => {
                  const thumbIdx = i - (THUMBNAIL_SLOTS - thumbnails.length);
                  const dataUrl = thumbIdx >= 0 ? thumbnails[thumbIdx] : null;
                  return (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        height: "100%",
                        backgroundImage: dataUrl ? `url(${dataUrl})` : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundColor: "#111",
                        borderRight: i < THUMBNAIL_SLOTS - 1 ? "1px solid rgba(0,0,0,0.3)" : "none",
                      }}
                    />
                  );
                })}
              </Box>

              {/* Song color bars at bottom of filmstrip */}
              {timelineSongs.map((song, idx) => {
                const leftPct = (song.displayStart / maxBuffer) * 100;
                const widthPct = ((song.displayEnd - song.displayStart) / maxBuffer) * 100;
                if (widthPct <= 0) return null;
                return (
                  <Box
                    key={song._id + idx}
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: `${leftPct}%`,
                      width: `${widthPct}%`,
                      height: 6,
                      backgroundColor: song.color,
                      opacity: 0.85,
                      zIndex: 1,
                      pointerEvents: "none",
                    }}
                  />
                );
              })}

              {/* Dim overlay — outside clip range */}
              <Box sx={{
                position: "absolute", top: 0, left: 0,
                width: `${(displayRange[0] / maxBuffer) * 100}%`,
                height: "100%",
                backgroundColor: "rgba(0,0,0,0.7)",
                pointerEvents: "none",
              }} />
              <Box sx={{
                position: "absolute", top: 0, right: 0,
                width: `${((maxBuffer - displayRange[1]) / maxBuffer) * 100}%`,
                height: "100%",
                backgroundColor: "rgba(0,0,0,0.7)",
                pointerEvents: "none",
              }} />

              {/* Playback position indicator */}
              {displayPlaybackPos != null && (
                <Box sx={{
                  position: "absolute",
                  left: `${(displayPlaybackPos / maxBuffer) * 100}%`,
                  top: 0, bottom: 0, width: 2,
                  backgroundColor: "#fff",
                  boxShadow: "0 0 6px rgba(255,255,255,0.8)",
                  zIndex: 3,
                  pointerEvents: "none",
                  transition: "left 0.1s linear",
                }} />
              )}

              {/* Slider overlay — transparent track, drag handles at clip edges */}
              <Slider
                value={displayRange}
                onChange={handleRangeChange}
                min={0}
                max={maxBuffer}
                step={1}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${maxBuffer - v}s ago`}
                sx={{
                  position: "absolute",
                  top: 0, left: 0, right: 0,
                  height: "100%",
                  padding: "0 !important",
                  borderRadius: 0,
                  "& .MuiSlider-thumb": {
                    width: 8,
                    height: 56,
                    borderRadius: "3px",
                    backgroundColor: "rgba(255,215,0,0.9)",
                    border: "none",
                    boxShadow: "none",
                    "&:hover, &.Mui-focusVisible, &.Mui-active": {
                      boxShadow: "0 0 8px rgba(255,215,0,0.5)",
                    },
                  },
                  "& .MuiSlider-track": {
                    backgroundColor: "transparent",
                    border: "2px solid rgba(255,215,0,0.6)",
                    borderLeft: "none",
                    borderRight: "none",
                  },
                  "& .MuiSlider-rail": {
                    display: "none",
                  },
                  "& .MuiSlider-valueLabel": {
                    backgroundColor: "#333",
                    fontSize: "11px",
                  },
                }}
              />
            </Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "11px" }}>
                {formatSeconds(maxBuffer)} ago
              </Typography>
              <Typography sx={{ color: "rgba(255,215,0,0.9)", fontSize: "13px", fontWeight: 600 }}>
                {clipDuration}s clip
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "11px" }}>
                Now
              </Typography>
            </Stack>

            {/* Song legend — colored dots matching timeline bars */}
            {overlappingSongs.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 0.5 }}>
                {overlappingSongs.map((song, idx) => (
                  <Stack key={song._id + idx} direction="row" alignItems="center" gap={0.5}>
                    <Box sx={{
                      width: 8, height: 8, borderRadius: "50%",
                      backgroundColor: song.color, flexShrink: 0,
                    }} />
                    <Typography sx={{
                      color: "rgba(255,255,255,0.7)", fontSize: "11px",
                      maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {song.title || "Unknown"}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "11px", mt: 0.5 }}>
                No music in clip range
              </Typography>
            )}
          </Box>
        )}

        {/* Title input */}
        <Box>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", mb: 0.5 }}>
            Title
          </Typography>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Name your clip..."
            maxLength={100}
            style={{
              width: "100%",
              padding: "10px 14px",
              backgroundColor: "#2A2A2A",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "8px",
              color: "#fff",
              fontSize: "14px",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </Box>

        {/* Playlist picker */}
        <Box>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", mb: 0.5 }}>
            Add to Playlist
          </Typography>
          <Stack
            spacing={0.5}
            sx={{
              maxHeight: "180px",
              overflow: "auto",
              borderRadius: "8px",
            }}
          >
            {loading ? (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <CircularProgress size={24} sx={{ color: "rgba(255,215,0,0.6)" }} />
              </Box>
            ) : playlists.length > 0 ? (
              playlists.map((p) => (
                <Stack
                  key={p._id}
                  direction="row"
                  alignItems="center"
                  gap="10px"
                  onClick={() =>
                    setSelectedPlaylist(
                      selectedPlaylist === p._id ? null : p._id
                    )
                  }
                  sx={{
                    cursor: "pointer",
                    borderRadius: "8px",
                    background:
                      selectedPlaylist === p._id ? "#333" : "#222",
                    padding: "8px 12px",
                    "&:hover": { background: "#333" },
                    transition: "background 0.15s",
                  }}
                >
                  <Typography sx={{ color: "#fff", fontSize: "13px", flex: 1 }}>
                    {p.title}
                  </Typography>
                  {p.private && (
                    <LockIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.3)" }} />
                  )}
                  {selectedPlaylist === p._id && (
                    <DoneIcon
                      sx={{
                        fontSize: 16,
                        color: "#000",
                        backgroundColor: "rgba(255,215,0,0.9)",
                        borderRadius: "50%",
                        padding: "2px",
                      }}
                    />
                  )}
                </Stack>
              ))
            ) : (
              <NoData lable="No playlists found" />
            )}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", gap: "10px", pb: 2.5, px: 3 }}>
        <Button
          onClick={onClose}
          disabled={isRecording}
          sx={{
            color: "rgba(255,255,255,0.5)",
            borderColor: "rgba(255,255,255,0.15)",
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "13px",
            px: 3,
            "&:hover": {
              borderColor: "rgba(255,255,255,0.3)",
              background: "rgba(255,255,255,0.04)",
            },
          }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handlePublish}
          disabled={!canPublish}
          sx={{
            background: "rgba(255,215,0,0.12)",
            color: "rgba(255,215,0,0.9)",
            border: "1px solid rgba(255,215,0,0.35)",
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            px: 3,
            "&:hover": {
              background: "rgba(255,215,0,0.2)",
              borderColor: "rgba(255,215,0,0.6)",
            },
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.2)",
              borderColor: "rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.04)",
            },
          }}
        >
          Publish Clip
        </Button>
      </DialogActions>
    </Dialog>
  );
}
